// Vocabulary layer - the string indirection the plan requires from day one.
// Technical mode shows framework terms; simple mode shows the friendly
// register and collapses long text harder. Verdict language (PURSUE/KILL,
// the Skeptic) is never softened.
//
// The mode is a per-user preference stored in auth.db and delivered with
// /api/auth/me. When the server does not carry it (dev mode with auth
// disabled, or an older server), the device-local localStorage value applies
// so the toggle still works.

import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { GateNumber, VerdictNorm } from "@ideacrunch/shared";
import { useAuthState, type AuthState } from "./auth-ui";

interface VocabContextValue {
  simple: boolean;
  setSimple: (v: boolean) => void;
}

const VocabContext = createContext<VocabContextValue>({
  simple: false,
  setSimple: () => {},
});

export function VocabProvider({ children }: { children: ReactNode }) {
  const auth = useAuthState();
  const queryClient = useQueryClient();
  // Device fallback. The legacy "ic-owner-mode=off" key meant the friendly
  // register, which maps to simple mode.
  const [localSimple, setLocalSimple] = useState(
    () =>
      localStorage.getItem("ic-simple-mode") === "on" ||
      (localStorage.getItem("ic-simple-mode") === null &&
        localStorage.getItem("ic-owner-mode") === "off"),
  );
  const account = auth.data?.account;
  const serverMode = account?.mode;
  const simple =
    serverMode !== undefined ? serverMode === "simple" : localSimple;

  const value = useMemo(
    () => ({
      simple,
      setSimple: (v: boolean) => {
        localStorage.setItem("ic-simple-mode", v ? "on" : "off");
        setLocalSimple(v);
        if (account && serverMode !== undefined) {
          const mode: "simple" | "technical" = v ? "simple" : "technical";
          queryClient.setQueryData<AuthState>(["auth"], (prev) =>
            prev?.account
              ? { ...prev, account: { ...prev.account, mode } }
              : prev,
          );
          void fetch(`/api/auth/users/${account.id}/mode`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ mode }),
          }).finally(() => {
            void queryClient.invalidateQueries({ queryKey: ["auth"] });
          });
        }
      },
    }),
    [simple, account, serverMode, queryClient],
  );
  return <VocabContext.Provider value={value}>{children}</VocabContext.Provider>;
}

export function useVocab() {
  const { simple, setSimple } = useContext(VocabContext);
  // Legacy name kept for the many call sites: "owner mode" = technical mode.
  const ownerMode = !simple;

  const gateTitle = (gate: GateNumber): string => {
    const friendly = [
      "Confirm the framing",
      "Pick the direction",
      "Approve the plan",
    ][gate]!;
    return ownerMode ? `Gate ${gate}: ${friendly}` : friendly;
  };

  const verdictGloss = (norm: VerdictNorm | null, raw: string): string => {
    if (ownerMode || norm === null) return raw || "ungraded";
    const gloss: Partial<Record<VerdictNorm, string>> = {
      SUPPORTED: "Held",
      WEAK: "Thin",
      REFUTED: "Broken",
      UNVERIFIABLE: "Can't check",
    };
    return gloss[norm] ?? raw;
  };

  const laneWord = ownerMode ? "lane" : "research track";
  const skepticReviewTitle = ownerMode ? "Skeptic review" : "Stress-test report";
  const residueTitle = ownerMode ? "Residue" : "Debrief";
  const killCriteriaTitle = ownerMode
    ? "Kill criteria"
    : "Deal-breakers (registered up front)";
  const steelmanTitle = ownerMode
    ? "Steelman, both directions"
    : "The strongest honest case, each way";

  return {
    simple,
    setSimple,
    ownerMode,
    gateTitle,
    verdictGloss,
    laneWord,
    skepticReviewTitle,
    residueTitle,
    killCriteriaTitle,
    steelmanTitle,
  };
}

export const KIND_TITLES: Record<string, { owner: string; user: string }> = {
  scout: { owner: "Scout report", user: "Idea scout" },
  intake: { owner: "Intake", user: "Framing" },
  plan: { owner: "Research plan", user: "Research plan" },
  research: { owner: "Research lane", user: "Research track" },
  "skeptic-review": { owner: "Skeptic review", user: "Stress-test report" },
  recommendation: { owner: "Recommendation", user: "Recommendation" },
  "implementation-plan": { owner: "Implementation plan", user: "Roadmap" },
  "plan-review": { owner: "Plan review", user: "Plan stress-test" },
  "next-steps": { owner: "Next steps", user: "Next steps" },
  residue: { owner: "Residue", user: "Debrief" },
};
